import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const veracode = defineConnector({
  identifier: "veracode",
  name: "Veracode",
  tagLine: "Application security testing",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ baseUrl }) =>
    exactUrlToRegex(baseUrl || "https://api.veracode.com"),
  auth: ({ configuration }) =>
    fetchTokenAuth({
      request: {
        url: {
          from: "literal",
          value:
            (configuration.baseUrl || "https://api.veracode.com")
              .trim()
              .replace(/\/+$/, "") + "/api/authn/v2/oauth2/token",
        },
        headers: [],
        basicAuth: {
          username: { from: "config", field: "clientId" },
          password: { from: "secret", field: "clientSecret" },
        },
        formBody: {
          grant_type: { from: "literal", value: "client_credentials" },
        },
      },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: {
        kind: "header",
        name: "Authorization",
        scheme: "Bearer",
        extraHeaders: [],
      },
    }),
  steps: [
    {
      title: "Set up Veracode",
      items: [
        {
          kind: "markdown",
          content:
            "In Veracode, open **API Credentials → Create API Credentials → OAuth Client**. Select the roles required for your workflows. Copy the client ID and secret, then enter the API host for your region. The connection test reads the authenticated principal. See [OAuth client credentials](https://docs.veracode.com/r/OAuth_Client_credentials) and [API authentication setup](https://docs.veracode.com/r/t_install_api_authen).",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "baseUrl",
          label: "API base URL",
          required: true,
          defaultValue: "https://api.veracode.com",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "Use api.veracode.com for Commercial, api.veracode.eu for Europe, or api.veracode.us for US Federal.",
        },
        {
          kind: "input",
          label: "OAuth Client ID",
          required: true,
          target: {
            kind: "configuration",
            field: "clientId",
          },
          helperText:
            "The Client ID generated when an OAuth Client Credentials credential is created in the Veracode Platform.",
        },
        {
          kind: "input",
          label: "OAuth Client Secret",
          required: true,
          target: {
            kind: "secrets",
            field: "clientSecret",
          },
          helperText:
            "The Client Secret generated with the OAuth Client Credentials credential. Store it securely and rotate it according to your organization’s policy.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url:
        (baseUrl || "https://api.veracode.com").trim().replace(/\/+$/, "") +
        "/api/authn/v2/principal",
      method: "GET",
      headers: { Accept: "application/json" },
    }),
  },
});
